import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import {
  getSingleValueForTasField,
  type FieldName,
} from '../../../../utils/tuition-assistance/fields.ts';
import {
  ANALYSIS_TIMEOUT_MS,
  TA_MAX_UPLOAD_FILE_SIZE_BYTES,
  analysisErrorTags,
  analysisTimeoutTags,
  evaluateAiValidation,
  extractAiResult,
  type ServiceResponsePayload,
} from '../../../../utils/tuition-assistance/ai-validation.ts';
import AiReadableFileTypes from '../../../../helpers/ai-readable-file-types.ts';
import {
  assetMatchesDocumentEntry,
  isLiveForClassification,
  untaggedLegacyUploads,
} from '../../../../utils/tuition-assistance/categorized-upload.ts';
import type { DocumentEntryLike } from '../../../../utils/tuition-assistance/categorized-upload.ts';
import { classificationLabelKey } from '../../../../utils/tuition-assistance/asset-classification.ts';
import { PhPlus, PhWarningCircle } from 'ember-phosphor-icons';
import Component from '@glimmer/component';
import { findRecord } from '@warp-drive/utilities/json-api';
import { deleteExistingRecord, saveRecord } from 'tio-data/requests/save-record';
import { updateTasAssetTagsRequest } from 'tio-data/requests/tas-asset';
import DiscardedList from './discarded-list.gts';
import DocumentRow from './document-row.gts';
import OtherDocumentsList from './other-documents-list.gts';
import PriorUploadsList from './prior-uploads-list.gts';
import type { DocumentAssetState, DocumentRowState, DocumentRowStatus } from './document-row.gts';
import type { DocumentType } from '../../../../types/tuition-assistance.ts';
import type CableService from '../../../../services/cable.ts';
import type DigitalAssetsService from '../../../../services/digital-assets.ts';
import type IntlService from 'ember-intl/services/intl';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

/** The three polymorphic models that can own TasAssets via a hasMany `tasAssets`. */
type Attachable = TasApplicationModel | TasCourseModel | TasProgramInstanceModel;

/** The attachable model names a caller passes as `@attachableType`. */
type AttachableType = 'tasApplication' | 'tasCourse' | 'tasProgramInstance';

/** TasAsset's inverse hasMany relationship keys. */
type AttachableRelationshipKey = 'tasProgramInstances' | 'tasCourses' | 'tasApplications';

/** Maps each attachable model name to TasAsset's matching hasMany relationship. */
const ATTACHABLE_RELATIONSHIP_KEY: Record<AttachableType, AttachableRelationshipKey> = {
  tasApplication: 'tasApplications',
  tasCourse: 'tasCourses',
  tasProgramInstance: 'tasProgramInstances',
};

const TA_MAX_UPLOAD_FILE_SIZE_MB = TA_MAX_UPLOAD_FILE_SIZE_BYTES / (1024 * 1024);

export interface CategorizedUploadSignature {
  Args: {
    attachable: Attachable;
    attachableType: AttachableType;
    programTemplate: TasProgramTemplateModel;
    /** Approval-step classification (e.g. "COMPLETION_DOCUMENTS"). Drives both
     * the asset's `classification` AND the lookup of required document types. */
    classification: string;
    /** Overrides the stage's template requirement, including an application evidence waiver. Defaults to the matching stage's required flag. */
    isRequired?: boolean;
    /** Rows to render instead of the classification's own template rows, for a section that merges several stages. Uploads are still filed under `classification`. */
    documentEntries?: DocumentEntryLike[];
    /** Admin-configured instruction text. When omitted, falls back to the
     * `${classification}_STRING` field on the program template. */
    instructions?: string;
    /**
     * Drops the section's own heading and its generic intro line, for hosts
     * that already title this surface. Leaves every control, the rows and the
     * counter untouched.
     */
    compact?: boolean;
    existingAssets?: TasAssetModel[];
    /**
     * Every asset reachable from this application — course-level, application-
     * level, and (type 2/3 only) program-instance-level. Anything in here that
     * the rows don't render is listed read-only as prior-step history. Optional:
     * surfaces that only ever hold one step's documents can omit it.
     */
    relatedAssets?: TasAssetModel[];
    disabled?: boolean;
    onUploadComplete?: () => void;
  };
  Element: HTMLDivElement;
}

type RecoveredState = Pick<DocumentRowState, 'status' | 'feedback' | 'flagKind'>;

interface RecoveredTagSave {
  asset: TasAssetModel;
  tags: Record<string, unknown>;
}

/**
 * Use for approval steps whose *_ATTACHMENT_FIELD has nonempty document_types. Unconfigured steps
 * use UploadAttachments.
 */
export default class CategorizedUpload extends Component<CategorizedUploadSignature> {
  @service declare cable: CableService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare intl: IntlService;
  @service declare store: StoreService;

  /**
   * Per-row state keyed by document type. Tracked as a plain object so a single
   * reassignment triggers re-render — Map mutations would not be reactive in
   * this glimmer version.
   */
  @tracked documentStates: Record<string, DocumentRowState> = {};

  /**
   * Soft-removed assets, kept in-memory for the lifetime of this component
   * instance to render the "Discarded:" tray (Figma B5+). Discarding here
   * destroys the attachment join on the server so the file no longer counts
   * toward the application; this list is purely UX history.
   */
  @tracked discardedAssets: TasAssetModel[] = [];

  /**
   * Extra rows added via "Add Item +". Always seeded as `general` so the
   * employee can attach supplemental documents that don't match a required
   * type. Each entry is an arbitrary key so multiples are allowed.
   */
  @tracked extraRowKeys: string[] = [];

  /**
   * Assets whose filing PATCH is in flight, by id. Their tray row stays put
   * until the server accepts (nothing is applied optimistically), so without
   * this the select stays live and a second pick would fire a competing write
   * built from the same original tags — server keeping whichever arrived last,
   * the client whichever resolved last, and the document rendering in both rows.
   */
  @tracked filingAssetIds: string[] = [];

  /**
   * Per-asset (uuid-keyed) timeout handles. Keyed by asset — not row — so a
   * second upload into the same row cannot orphan the first upload's timer.
   */
  private analysisTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Only assets started by this component have live subscriptions and timeouts. Persisted pending
   * tags are treated as unvalidated; teardown clears this set with its subscriptions.
   */
  #inFlightAssetUuids = new Set<string>();

  private pendingRecoveredTagSaves = new Map<string, RecoveredTagSave>();

  /**
   * The wizard unmounts this step as soon as the user advances; without
   * teardown the orphaned timers would fire against a destroyed component
   * (and could stamp timeout tags over a verdict persisted after remount).
   */
  willDestroy() {
    super.willDestroy();
    for (const timeoutId of this.analysisTimeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.analysisTimeouts.clear();
    for (const uuid of this.#inFlightAssetUuids) {
      this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: uuid });
    }
    this.#inFlightAssetUuids.clear();
  }

  private statusPriority(status: DocumentRowStatus) {
    switch (status) {
      case 'uploading':
      case 'analyzing':
        return 6;
      case 'flagged':
        return 5;
      case 'not_validated':
        return 4;
      case 'error':
        return 3;
      case 'accepted':
        return 2;
      case 'valid':
        return 1;
      default:
        return 0;
    }
  }

  private stateFromAssets(key: string, assets: TasAssetModel[]): DocumentRowState {
    if (assets.length === 0) {
      return { status: 'empty', asset: null, assets: [], assetStates: [], feedback: null };
    }

    const assetStates: DocumentAssetState[] = assets.map((asset) => ({
      asset,
      ...this.deriveStateFromAsset(asset, key),
    }));

    // Transient states take priority; otherwise one valid file satisfies the row while flagged files keep their own banners.
    const transient = assetStates.find((s) => s.status === 'uploading' || s.status === 'analyzing');
    const valid = assetStates.find((s) => s.status === 'valid');
    const accepted = assetStates.find((s) => s.status === 'accepted');
    const primary =
      transient ??
      valid ??
      accepted ??
      assetStates.reduce((worst, current) =>
        this.statusPriority(current.status) > this.statusPriority(worst.status) ? current : worst
      );

    return {
      status: primary.status,
      asset: primary.asset,
      assets,
      assetStates,
      feedback: primary.feedback,
      flagKind: primary.flagKind,
    };
  }

  get instructionsField(): FieldName {
    return `${this.args.classification}_STRING` as FieldName;
  }

  /**
   * Explicit instructions override the template field; guided wizards may combine instructions from
   * several stages.
   */
  get instructions(): string {
    return (
      this.args.instructions ??
      getSingleValueForTasField(this.instructionsField, this.args.programTemplate?.fields, '')
    );
  }

  /**
   * Suppress instructions duplicated by configured row labels; guidance and attachment-name fields
   * often contain identical text.
   */
  get showInstructions(): boolean {
    const instructions = this.instructions.trim().toLowerCase();
    if (!instructions) {
      return false;
    }
    return !this.documentEntries.some((entry) => entry.label.trim().toLowerCase() === instructions);
  }

  get isRequired() {
    if (this.args.isRequired !== undefined) return this.args.isRequired;
    switch (this.args.classification) {
      case 'PROGRAM_APPROVAL_DOCUMENTS':
        return this.args.programTemplate.requireAttachmentsForProgramApproval;
      case 'COURSE_APPROVAL_DOCUMENTS':
        return this.args.programTemplate.requireAttachmentsForCourseApproval;
      case 'COMPLETION_DOCUMENTS':
        return this.args.programTemplate.requireAttachmentsForCourseCompletion;
      default:
        return false;
    }
  }

  get requiredDocTypes(): DocumentType[] {
    return this.args.programTemplate.requiredDocumentTypesFor(this.args.classification);
  }

  /**
   * All admin-configured entries (label + type) for the current classification,
   * including `general` items. Used to render one row per attachment the admin
   * configured, with their custom label.
   */
  get documentEntries(): DocumentEntryLike[] {
    return (
      this.args.documentEntries ??
      this.args.programTemplate.documentEntriesFor(this.args.classification)
    );
  }

  /**
   * Uncategorized legacy uploads count toward submission even though no configured row can display
   * them.
   */
  get untaggedLegacyAssets() {
    return untaggedLegacyUploads(this.args.existingAssets ?? [], this.args.classification);
  }

  /** Tray rows, each carrying whether its own filing is mid-flight. */
  get otherDocumentRows() {
    return this.untaggedLegacyAssets.map((asset) => ({
      asset,
      isFiling: this.filingAssetIds.includes(this.assetKey(asset)),
    }));
  }

  private assetKey(asset: TasAssetModel) {
    return asset.id ?? asset.uuid;
  }

  private rowKeyFor(entry: DocumentEntryLike) {
    return entry.rowKey ?? entry.label;
  }

  private entryForRowKey(key: string) {
    return this.documentEntries.find((entry) => this.rowKeyFor(entry) === key);
  }

  private uploadRowKeyFor(key: string) {
    return this.entryForRowKey(key)?.label ?? key;
  }

  displayLabelFor = (entry: DocumentEntryLike) => {
    if (!entry.rowKey) return entry.label;
    const typeLabel = this.intl.t(`tas.attachments.document_types.${entry.type}.label`);
    return `${entry.label} (${typeLabel})`;
  };

  get fileUnderEntries() {
    return this.documentEntries.map((entry) => ({
      ...entry,
      label: this.displayLabelFor(entry),
    }));
  }

  /**
   * Live uploads not shown in a current row or the Other-documents tray. Includes earlier steps and
   * renamed-row orphans; excludes discarded uploads and duplicates.
   */
  get priorUploads() {
    const shownIds = new Set<string>();
    const claim = (assets: TasAssetModel[]) => {
      for (const asset of assets) shownIds.add(asset.id ?? asset.uuid);
    };
    for (const entry of this.documentEntries) claim(this.stateFor(this.rowKeyFor(entry)).assets);
    for (const rowKey of this.extraRowKeys) claim(this.stateFor(rowKey).assets);
    claim(this.untaggedLegacyAssets);

    // `relatedAssets` unions three relationships, so one asset attached to both
    // a course and the application would otherwise appear twice.
    const seen = new Set<string>(shownIds);
    const rows = [];
    for (const asset of this.args.relatedAssets ?? []) {
      const key = asset.id ?? asset.uuid;
      if (asset.attachmentDeletedAt !== null || seen.has(key)) continue;
      seen.add(key);
      rows.push({ asset, labelKey: classificationLabelKey(asset.classification) });
    }

    return rows.sort(
      (a, b) => new Date(a.asset.createdAt).getTime() - new Date(b.asset.createdAt).getTime()
    );
  }

  /**
   * Map from label → type for fast lookup when the label is used as a row key.
   */
  private get labelToType(): Map<string, DocumentType> {
    const m = new Map<string, DocumentType>();
    for (const entry of this.documentEntries) {
      m.set(this.rowKeyFor(entry), entry.type);
      if (!m.has(entry.label)) m.set(entry.label, entry.type);
    }
    return m;
  }

  get employeeName(): string {
    const attachable = this.args.attachable;
    // TasApplication → tasProgramInstance → employee → person
    const programInstance =
      'tasProgramInstance' in attachable
        ? (attachable as TasApplicationModel).tasProgramInstance
        : null;
    const person = programInstance?.employee?.person;
    return person?.fullName ?? '';
  }

  private get expectedInstitutionName() {
    const attachable = this.args.attachable;
    if ('institutionName' in attachable) return attachable.institutionName;
    if ('tasProgramInstance' in attachable)
      return attachable.tasProgramInstance?.institutionName ?? '';
    if ('tasApplication' in attachable) {
      return attachable.tasApplication?.tasProgramInstance?.institutionName ?? '';
    }
    return '';
  }

  private get expectedCourseNames() {
    const attachable = this.args.attachable;
    if ('courseName' in attachable) return [attachable.courseName].filter(Boolean);
    if ('activeCourses' in attachable) {
      return attachable.activeCourses.map((course) => course.courseName).filter(Boolean);
    }
    if ('tasApplications' in attachable) {
      return attachable.tasApplications
        .flatMap((application) => application.activeCourses.map((course) => course.courseName))
        .filter(Boolean);
    }
    return [];
  }

  /**
   * Returns all matching live assets, including pre-mount uploads; unmatched rows are empty. Label-
   * keyed rows resolve through the declared document-type map.
   */
  stateFor = (key: string): DocumentRowState => {
    const tracked = this.documentStates[key];
    if (tracked) return tracked;

    // Share row-key and declared-type matching with the submission gate to keep visible files and eligibility aligned.
    const mappedType = this.labelToType.get(key);
    const matchType = (mappedType ?? key) as DocumentType;
    const entry = this.entryForRowKey(key);

    const existingAssets = (this.args.existingAssets ?? []).filter(
      (a) =>
        isLiveForClassification(a, this.args.classification) &&
        assetMatchesDocumentEntry(a, entry ?? { label: key, type: matchType })
    );
    return this.stateFromAssets(key, existingAssets);
  };

  /**
   * Every asset the component can see, deduplicated. `existingAssets` is the
   * application's own; `relatedAssets` unions the course/instance/assistance
   * relationships. Both arrive unfiltered, so discarded files are in here.
   */
  private get allVisibleAssets(): TasAssetModel[] {
    const seen = new Set<string>();
    const all = [...(this.args.existingAssets ?? []), ...(this.args.relatedAssets ?? [])];

    return all.filter((asset) => {
      const key = asset.id ?? asset.uuid;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Files discarded from a row, read off the server's soft-delete marker rather
   * than session state — `discardedAssets` only holds what was discarded since
   * this component mounted, so anything removed on an earlier visit was
   * invisible.
   *
   * Matched with the same predicate as the live files, so a discarded file
   * appears under the row it was actually filed against. Never feeds
   * `stateFor`, `uploadedCount` or the submission gate: a removed file
   * satisfies nothing.
   */
  discardedFor = (key: string): TasAssetModel[] => {
    const mappedType = this.labelToType.get(key);
    const matchType = (mappedType ?? key) as DocumentType;
    const entry = this.entryForRowKey(key);

    return this.allVisibleAssets
      .filter(
        (asset) =>
          asset.attachmentDeletedAt !== null &&
          asset.classification === this.args.classification &&
          assetMatchesDocumentEntry(asset, entry ?? { label: key, type: matchType })
      )
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  };

  /**
   * The session tray keeps only what no row will show inline, so a discarded
   * file is never rendered twice.
   */
  get unfiledDiscardedAssets(): TasAssetModel[] {
    const inlineIds = new Set<string>();
    for (const entry of this.documentEntries) {
      for (const asset of this.discardedFor(this.rowKeyFor(entry))) {
        inlineIds.add(asset.id ?? asset.uuid);
      }
    }
    for (const rowKey of this.extraRowKeys) {
      for (const asset of this.discardedFor(rowKey)) inlineIds.add(asset.id ?? asset.uuid);
    }

    return this.discardedAssets.filter((asset) => !inlineIds.has(asset.id ?? asset.uuid));
  }

  get requiredEntries() {
    return this.documentEntries.filter((entry) => entry.isRequired !== false);
  }

  get uploadedCount(): number {
    return this.requiredEntries.filter((entry) => {
      const state = this.stateFor(this.rowKeyFor(entry));
      return state.status !== 'empty';
    }).length;
  }

  get flaggedCount(): number {
    return this.documentEntries.reduce(
      (count, entry) =>
        count +
        this.stateFor(this.rowKeyFor(entry)).assetStates.filter((assetState) =>
          ['flagged', 'not_validated', 'error'].includes(assetState.status)
        ).length,
      0
    );
  }

  /** Map persisted tag state back to a row state when re-rendering. */
  private deriveStateFromAsset(asset: TasAssetModel, key: string): RecoveredState {
    const aiResult = extractAiResult(asset.assetSummary?.summary);
    if (aiResult && asset.aiValidationStatus === 'pending') {
      const outcome = this.validationOutcomeForAiResult(key, aiResult);
      this.scheduleRecoveredTagSave(asset, key, outcome.tags);
      return {
        status: outcome.status,
        feedback: outcome.feedback,
        flagKind: outcome.flagKind,
      };
    }

    const displayStatus = asset.aiValidationDisplayStatus;
    const tagStatus = asset.aiValidationStatus;
    if (tagStatus === 'valid') return { status: 'valid', feedback: null };
    if (tagStatus === 'accepted') return { status: 'accepted', feedback: null };
    if (displayStatus === 'not_validated') return { status: 'not_validated', feedback: null };
    if (tagStatus === 'flagged' && aiResult) {
      const outcome = this.validationOutcomeForAiResult(key, aiResult);
      return {
        status: outcome.status,
        feedback: outcome.feedback,
        flagKind: outcome.flagKind,
      };
    }
    if (tagStatus === 'flagged')
      return { status: 'flagged', feedback: asset.aiValidationFeedback ?? null };
    if (tagStatus === 'error') return { status: 'error', feedback: asset.aiValidationFeedback };
    if (tagStatus === 'pending') {
      // Persisted pending tags lack a live subscription; offer recovery instead of an indefinite spinner.
      if (this.#inFlightAssetUuids.has(asset.uuid)) {
        return { status: 'analyzing', feedback: null };
      }
      return { status: 'not_validated', feedback: null };
    }
    // No AI tag yet (legacy upload): treat as a previously-uploaded file.
    return { status: 'valid', feedback: null };
  }

  private scheduleRecoveredTagSave(
    asset: TasAssetModel,
    key: string,
    tags: Record<string, unknown>
  ) {
    this.pendingRecoveredTagSaves.set(`${asset.id ?? asset.uuid}:${key}`, { asset, tags });

    // eslint-disable-next-line ember/no-runloop
    scheduleOnce('afterRender', this, this.persistRecoveredTags);
  }

  private persistRecoveredTags = () => {
    const pending = [...this.pendingRecoveredTagSaves.values()];
    this.pendingRecoveredTagSaves.clear();

    for (const { asset, tags } of pending) {
      if (asset.aiValidationStatus !== 'pending') continue;

      asset.tags = tags;
      void saveRecord(this.store, asset).catch((e: unknown) => {
        console.warn('Failed to persist recovered AI validation tags', e);
      });
    }
  };

  private recoverAnalysisFromAsset = async (docType: string, asset: TasAssetModel) => {
    try {
      await this.store.request(
        findRecord<TasAssetModel>('tas-asset', asset.id!, {
          include: 'asset-summary',
          reload: true,
        })
      );
    } catch (e) {
      console.warn('Failed to reload asset for recovered AI validation', e);
      return false;
    }
    if (this.isDestroying || this.isDestroyed) return false;

    const aiResult = extractAiResult(asset.assetSummary?.summary);
    if (!aiResult) return false;

    const outcome = this.validationOutcomeForAiResult(docType, aiResult);
    asset.tags = outcome.tags;
    try {
      await saveRecord(this.store, asset);
    } catch (e) {
      console.warn('Failed to persist recovered AI validation tags', e);
    }
    if (this.isDestroying || this.isDestroyed) return true;

    this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
    this.args.onUploadComplete?.();
    return true;
  };

  private clearAnalysisTimeout(assetUuid: string) {
    const existingTimeout = this.analysisTimeouts.get(assetUuid);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.analysisTimeouts.delete(assetUuid);
    }
  }

  /**
   * Evaluate an AI result via the shared evaluator (utils/tuition-assistance/
   * ai-validation.ts) — the admin approval drawers run the exact same logic.
   */
  private validationOutcomeForAiResult(docType: string, aiResult: Record<string, unknown>) {
    return evaluateAiValidation(aiResult, {
      declaredType: this.actualDocType(docType),
      uploadRowKey: this.uploadRowKeyFor(docType),
      employeeName: this.employeeName,
      expectedInstitutionName: this.expectedInstitutionName,
      expectedCourseNames: this.expectedCourseNames,
      translate: (key: string, options?: Record<string, string>) => this.intl.t(key, options),
    });
  }

  /**
   * Persist a single row's state by replacing the entire object. Required
   * because a tracked `Record` only re-renders consumers when reassigned.
   */
  private updateDocState(docType: string, partial: Partial<DocumentRowState>) {
    this.documentStates = {
      ...this.documentStates,
      [docType]: {
        ...this.stateFor(docType),
        ...partial,
      },
    };
  }

  private appendAssetToDocState(
    docType: string,
    asset: TasAssetModel,
    partial: Partial<DocumentRowState>
  ) {
    const current = this.stateFor(docType);
    const assets = current.assets.some((existing) => existing === asset || existing.id === asset.id)
      ? current.assets
      : [...current.assets, asset];

    this.documentStates = {
      ...this.documentStates,
      [docType]: {
        ...this.stateFromAssets(docType, assets),
        asset,
        ...partial,
      },
    };
  }

  private refreshDocStateFromAssets(docType: string, assets: TasAssetModel[]) {
    this.documentStates = {
      ...this.documentStates,
      [docType]: this.stateFromAssets(docType, assets),
    };
  }

  /**
   * Extra row keys start with "extra-"; for those the actual document type
   * persisted on the asset is always "general". For admin-configured label keys,
   * look up the corresponding type from the documentEntries map.
   */
  private actualDocType = (key: string): DocumentType => {
    if (key.startsWith('extra-')) return 'general';
    const mapped = this.labelToType.get(key);
    if (mapped) return mapped;
    // Fallback: treat the key as a literal DocumentType (legacy path).
    return key as DocumentType;
  };

  handleUpload = async (docType: string, file: unknown) => {
    this.updateDocState(docType, { status: 'uploading', feedback: null });

    // 1. Create the TasAsset record from the S3 upload details and tag it with
    //    the employee's declared type plus a pending validation marker.
    const asset = this.digitalAssets.createAssetFromS3Details(
      'tasAsset',
      file as Parameters<DigitalAssetsService['createAssetFromS3Details']>[1]
    );
    asset.classification = this.args.classification;
    const actualType = this.actualDocType(docType);
    asset.tags = {
      uploadRowKey: this.uploadRowKeyFor(docType),
      employeeSelectedType: actualType,
      aiValidationStatus: actualType === 'general' ? 'valid' : 'pending',
      aiValidationDisplayStatus: actualType === 'general' ? 'valid' : 'pending',
      skipAiAnalysis: String(actualType === 'general'),
    };

    // 2. Link to the attachable side of the relationship. Saves any new parent
    //    records first so the polymorphic relationship has IDs to point at.
    const attachable: Attachable = this.args.attachable;
    const relationshipKey = ATTACHABLE_RELATIONSHIP_KEY[this.args.attachableType];

    if (attachable.isNew) {
      // Only TasCourse has a `tasApplication` belongsTo; only TasApplication has
      // `tasProgramInstance`. Narrow the union before access.
      if ('tasApplication' in attachable && attachable.tasApplication?.isNew) {
        await saveRecord(this.store, attachable.tasApplication);
      }
      if ('tasProgramInstance' in attachable && attachable.tasProgramInstance?.isNew) {
        await saveRecord(this.store, attachable.tasProgramInstance);
      }
      await saveRecord(this.store, attachable);
    }
    // hasMany ManyArrays whose element types differ per variant; cast to the
    // loosest common element shape for the structurally-identical push.
    (asset[relationshipKey] as Attachable[]).push(attachable);
    attachable.tasAssets.push(asset);

    await saveRecord(this.store, asset);
    await saveRecord(this.store, attachable);

    if (actualType === 'general') {
      this.appendAssetToDocState(docType, asset, { status: 'valid', feedback: null });
      this.args.onUploadComplete?.();
      return;
    }

    // 3. Subscribe to the ActionCable channel using the asset UUID. The AI
    //    agent broadcasts on this same `request_id` once Gemini finishes.
    this.#inFlightAssetUuids.add(asset.uuid);
    const channelParams = {
      channel: 'ServiceResponseChannel',
      request_id: asset.uuid,
    };
    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        this.handleAiResponse(docType, asset, data as ServiceResponsePayload);
      },
    });

    this.appendAssetToDocState(docType, asset, { status: 'analyzing', feedback: null });

    // 4. Fall back to "not validated" if the AI takes too long.
    //    The employee can still use the document via "Use Anyways".
    //    Timers are per-asset so parallel uploads into one row each keep theirs.
    const timeoutId = setTimeout(() => {
      void (async () => {
        this.analysisTimeouts.delete(asset.uuid);
        // Already settled by the broadcast, removed, or torn down — nothing to do.
        if (!this.#inFlightAssetUuids.has(asset.uuid)) return;

        this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: asset.uuid });
        this.#inFlightAssetUuids.delete(asset.uuid);

        const recovered = await this.recoverAnalysisFromAsset(docType, asset);
        if (this.isDestroying || this.isDestroyed) return;
        // Re-check the asset itself: a broadcast (or a remount's use-anyways)
        // may have landed a verdict while the recovery reload was in flight —
        // never stamp timeout tags over a settled verdict.
        if (!recovered && asset.aiValidationStatus === 'pending') {
          asset.tags = analysisTimeoutTags(
            asset.tags,
            asset.employeeSelectedType ?? this.actualDocType(docType)
          );
          void saveRecord(this.store, asset).catch((e: unknown) => {
            console.warn('Failed to persist timeout validation status', e);
          });
          this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
        }
      })();
    }, ANALYSIS_TIMEOUT_MS);
    this.analysisTimeouts.set(asset.uuid, timeoutId);
  };

  handleAiResponse = async (
    docType: string,
    asset: TasAssetModel,
    data: ServiceResponsePayload
  ) => {
    this.clearAnalysisTimeout(asset.uuid);

    // Always unsubscribe — the AI agent only broadcasts once per asset.
    this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: asset.uuid });
    this.#inFlightAssetUuids.delete(asset.uuid);

    const summary = data?.data?.summary;
    if (!summary) {
      asset.tags = analysisErrorTags(
        asset.tags,
        asset.employeeSelectedType ?? this.actualDocType(docType),
        (key: string, options?: Record<string, string>) => this.intl.t(key, options)
      );
      try {
        await saveRecord(this.store, asset);
      } catch (e) {
        console.warn('Failed to persist AI validation error status', e);
      }
      this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
      return;
    }

    // The AI response wraps the Gemini result inside a JSONAPI resource:
    //   data.summary = { id, type, attributes: { summary: { result: { ...ai fields } } } }
    // Unwrap to get the raw AI analysis. Fall back to the top-level summary
    // in case the payload arrives pre-flattened.
    const aiResult = extractAiResult(summary);

    if (!aiResult) {
      asset.tags = analysisErrorTags(
        asset.tags,
        asset.employeeSelectedType ?? this.actualDocType(docType),
        (key: string, options?: Record<string, string>) => this.intl.t(key, options)
      );
      try {
        await saveRecord(this.store, asset);
      } catch (e) {
        console.warn('Failed to persist AI validation error status', e);
      }
      if (this.isDestroying || this.isDestroyed) return;
      this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
      return;
    }

    const outcome = this.validationOutcomeForAiResult(docType, aiResult);

    asset.tags = outcome.tags;
    try {
      await saveRecord(this.store, asset);
    } catch (e) {
      // Persisting tags is best-effort; the UI still reflects the result. We
      // surface a console warning rather than blocking the employee.
      console.warn('Failed to persist AI validation tags', e);
    }
    if (this.isDestroying || this.isDestroyed) return;

    this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);

    this.args.onUploadComplete?.();
  };

  handleRemove = async (docType: string, assetToRemove?: TasAssetModel) => {
    const state = this.stateFor(docType);
    const asset = assetToRemove ?? state.asset;

    // Clean up the removed asset's in-flight subscription / timer.
    if (asset) {
      this.clearAnalysisTimeout(asset.uuid);
      this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: asset.uuid });
      this.#inFlightAssetUuids.delete(asset.uuid);
    }

    if (!asset) {
      this.updateDocState(docType, {
        status: 'empty',
        asset: null,
        assets: [],
        assetStates: [],
        feedback: null,
        flagKind: undefined,
      });
      return;
    }
    // Capture for the discarded tray BEFORE destroying the join — the asset
    // still has its filename loaded at this point.
    const discardedSnapshot = asset;
    // Fresh uploads may lack attachments; only an explicit include can populate the join needed for deletion.
    let attachment = asset.attachments[0];
    if (!attachment) {
      try {
        await this.store.request(
          findRecord<TasAssetModel>('tas-asset', asset.id!, {
            reload: true,
            include: 'attachments',
          })
        );
        attachment = asset.attachments[0];
      } catch (e) {
        console.warn('Failed to reload asset for attachment lookup', e);
      }
    }
    // Keep the row when no deletion persisted; the live asset still satisfies the submission gate.
    if (!attachment) {
      console.warn('No attachment found for asset; discard not persisted', asset);
      return;
    }
    try {
      await deleteExistingRecord(this.store, attachment);
      // Refreshes `attachmentDeletedAt`, which is what marks the asset
      // non-live for the upload rows and the wizard's submission gate.
      await this.store.request(
        findRecord<TasAssetModel>('tas-asset', asset.id!, {
          reload: true,
          include: 'attachments',
        })
      );
    } catch (e) {
      console.warn('Failed to remove attachment', e);
      return;
    }
    this.discardedAssets = [...this.discardedAssets, discardedSnapshot];
    this.refreshDocStateFromAssets(
      docType,
      state.assets.filter((existing) => existing !== asset && existing.id !== asset.id)
    );
  };

  /**
   * "Use anyways" collapses the flagged/not-validated banner into the standard
   * uploaded state without removing the asset. The validation tags persist on
   * the asset so reviewers still see the warning later.
   */
  handleUseAnyways = async (docType: string, assetToAccept?: TasAssetModel) => {
    const state = this.stateFor(docType);
    const asset = assetToAccept ?? state.asset;
    if (asset) {
      asset.tags = {
        uploadRowKey: asset.uploadRowKey ?? this.uploadRowKeyFor(docType),
        employeeSelectedType: asset.employeeSelectedType ?? this.actualDocType(docType),
        aiDetectedTypes: asset.aiDetectedTypes,
        aiValidationStatus: 'accepted',
        aiValidationDisplayStatus: 'accepted',
        aiValidationFeedback: asset.aiValidationFeedback,
        aiTypeMatch: String(asset.aiTypeMatch ?? ''),
        aiIdentityMatch: String(asset.aiIdentityMatch ?? ''),
      };
      try {
        await saveRecord(this.store, asset);
      } catch (e) {
        console.warn('Failed to persist accepted status', e);
      }
    }
    this.refreshDocStateFromAssets(docType, state.assets);
  };

  /**
   * Adds a new free-form upload slot. Always typed `general` so the AI agent
   * still classifies it but no required-type slot is reserved.
   */
  handleAddItem = () => {
    this.extraRowKeys = [...this.extraRowKeys, `extra-${this.extraRowKeys.length}-${Date.now()}`];
  };

  /**
   * Files an untagged legacy upload under a configured entry: the employee
   * asserts what the document is, so it moves into that row and counts toward
   * the requirement. Tagging mirrors the categorized flows — `general` is
   * `valid` with analysis skipped (the drawer's convention); AI-classifiable
   * types get use-anyways semantics (`accepted`), since analysis only
   * dispatches at create and will never run for a pre-existing file.
   *
   * Persist FIRST, then apply to the record. `tags` drives `uploadRowKey` /
   * `employeeSelectedType`, which the rows here and the submission gates
   * (`hasRequiredCategorizedUploads`, read from the show page and the wizard)
   * all consume synchronously — and `Select` does not await `@onChange`. A
   * mutate-then-save would therefore make the document look filed, and count
   * toward its requirement, for the whole flight of the PATCH: long enough for
   * an employee to submit against evidence the server never accepted. Going
   * through an explicit-body builder instead of `saveRecord` is what makes
   * this possible — `saveExistingRecord` serializes the record's dirty state,
   * so with it the mutation IS the payload. Nothing observable changes until
   * the server has said yes, which is also why there is no rollback path.
   *
   * Because the row therefore stays in the tray for that whole window, filing
   * is guarded per asset: a second pick would otherwise build its payload from
   * the same original tags and race the first, leaving the server on whichever
   * request arrived last and this component on whichever promise resolved last
   * — and, since each resolution writes its own row's state, showing the
   * document under two categories at once. The select is disabled to match.
   */
  handleFileUnder = async (asset: TasAssetModel, label: string) => {
    const entry = this.entryForRowKey(label);
    if (!entry) return;

    const assetKey = this.assetKey(asset);
    if (this.filingAssetIds.includes(assetKey)) return;

    // Spread rather than replace: `tags` is a free-form JSON column, and these
    // assets predate the categorized rows — they lack the two placement tags
    // but may well carry unrelated metadata from whatever surface created them.
    // A wholesale replacement would silently drop it on the PATCH.
    const nextTags = {
      ...(asset.tags ?? {}),
      uploadRowKey: entry.label,
      employeeSelectedType: entry.type,
      aiValidationStatus: entry.type === 'general' ? 'valid' : 'accepted',
      aiValidationDisplayStatus: entry.type === 'general' ? 'valid' : 'accepted',
      skipAiAnalysis: 'true',
    };

    this.filingAssetIds = [...this.filingAssetIds, assetKey];
    try {
      await this.store.request(updateTasAssetTagsRequest(asset.id as string, nextTags));
    } catch (e) {
      console.warn('Failed to file document under category', e);
      return;
    } finally {
      // Cleared even on failure, so a document whose PATCH was rejected can be
      // filed again rather than being stranded behind a disabled control.
      this.filingAssetIds = this.filingAssetIds.filter((key) => key !== assetKey);
    }
    if (this.isDestroying || this.isDestroyed) return;

    asset.tags = nextTags;

    const rowAssets = this.stateFor(label).assets;
    this.refreshDocStateFromAssets(
      label,
      rowAssets.includes(asset) ? rowAssets : [...rowAssets, asset]
    );
  };

  <template>
    <div class="tio-col" ...attributes>
      {{#if this.showInstructions}}
        <p
          class="tio-text-sm-medium tio-fg-primary whitespace-pre-line"
          data-test-categorized-upload-instructions
        >
          {{this.instructions}}
        </p>
      {{/if}}
      {{#unless @compact}}
        <p class="text-sm tio-fg-muted">
          {{t (if this.isRequired "tas.attachments.intro" "tas.attachments.optional_intro")}}
        </p>
      {{/unless}}
      {{! Show shared upload limits once because individual row controls have no room for hints. }}
      <p class="text-xs tio-fg-muted" data-test-categorized-upload-formats>
        {{AiReadableFileTypes "display"}}
        ·
        {{t
          "file_upload.max_file_size"
          maxSize=TA_MAX_UPLOAD_FILE_SIZE_MB
          default="Max file size {maxSize}MB"
        }}
      </p>

      <div class="tio-row-between pb-1">
        {{#if @compact}}
          <span></span>
        {{else}}
          <h4 class="tio-text-sm-semibold uppercase tracking-wider tio-fg-muted">
            {{t
              (if
                this.isRequired
                "tas.attachments.required_documents"
                "tas.attachments.optional_documents"
              )
            }}
          </h4>
        {{/if}}
        <button
          type="button"
          class="tio-inline-row-extra-tight tio-text-sm-medium tio-fg-info hover:tio-fg-info-h"
          {{on "click" this.handleAddItem}}
          data-test-add-item
        >
          {{t "tas.attachments.add_item"}}
          <PhPlus @size="14" />
        </button>
      </div>

      <div class="tio-col-tight">
        {{#each this.documentEntries as |entry|}}
          {{#let (if entry.rowKey entry.rowKey entry.label) as |rowKey|}}
            <DocumentRow
              @docType={{entry.type}}
              @label={{this.displayLabelFor entry}}
              @isOptional={{eq entry.isRequired false}}
              @state={{this.stateFor rowKey}}
              @discarded={{this.discardedFor rowKey}}
              @onUpload={{fn this.handleUpload rowKey}}
              @onRemove={{fn this.handleRemove rowKey}}
              @onUseAnyways={{fn this.handleUseAnyways rowKey}}
              @disabled={{@disabled}}
            />
          {{/let}}
        {{/each}}
        {{#each this.extraRowKeys as |rowKey|}}
          {{! Extra general rows are validated but never satisfy required types; unique keys keep their state separate. }}
          <DocumentRow
            @docType="general"
            @state={{this.stateFor rowKey}}
            @discarded={{this.discardedFor rowKey}}
            @onUpload={{fn this.handleUpload rowKey}}
            @onRemove={{fn this.handleRemove rowKey}}
            @onUseAnyways={{fn this.handleUseAnyways rowKey}}
            @disabled={{@disabled}}
          />
        {{/each}}
      </div>

      <OtherDocumentsList
        @rows={{this.otherDocumentRows}}
        @entries={{this.fileUnderEntries}}
        @onFile={{this.handleFileUnder}}
        @disabled={{@disabled}}
      />

      <PriorUploadsList @rows={{this.priorUploads}} />

      <DiscardedList @assets={{this.unfiledDiscardedAssets}} />

      <div class="tio-row-between text-sm">
        <span class="tio-fg-muted" data-test-counter>
          {{#if this.isRequired}}
            {{this.uploadedCount}}
            /
            {{this.requiredEntries.length}}
            {{t "tas.attachments.required"}}
          {{else}}
            {{t "tas.attachments.optional"}}
          {{/if}}
        </span>
        {{#if this.flaggedCount}}
          <span
            class="tio-inline-row-extra-tight font-semibold tio-fg-danger-body"
            data-test-flagged-count
          >
            <PhWarningCircle @size="14" @weight="fill" />
            {{t "tas.attachments.flagged_count" count=this.flaggedCount}}
          </span>
        {{/if}}
      </div>
    </div>
  </template>
}
